import { types as sdkTypes } from '../../util/sdkLoader';
import React from 'react';

// Utils
import { convertUnitToSubUnit, formatMoney, unitDivisor } from '../../util/currency';
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import {
  getPuertoRicoMunicipalityName,
  getPuertoRicoMunicipalityOptions,
} from '../../util/puertoRicoMunicipalities';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';
import css from './CustomListingFields.module.css';

const { Money } = sdkTypes;

const formatTime12Hour = time => {
  const [hourString, minute = '00'] = (time || '').split(':');
  const hour24 = Number(hourString);

  if (!Number.isInteger(hour24)) {
    return time;
  }

  const hour12 = hour24 % 12 || 12;
  const period = hour24 < 12 ? 'AM' : 'PM';

  return `${hour12}:${minute} ${period}`;
};

const formatRequestedTimeWindow = value => {
  const [start, end] = (value || '').split('-');

  return start && end
    ? {
        start: formatTime12Hour(start),
        end: formatTime12Hour(end),
      }
    : null;
};

const formatBudgetAvailable = (value, intl, marketplaceCurrency) => {
  if (!marketplaceCurrency) {
    return value;
  }

  const budgetAsNumber = Number(value);
  if (!Number.isFinite(budgetAsNumber)) {
    return value;
  }

  const valueInSubunits = convertUnitToSubUnit(budgetAsNumber, unitDivisor(marketplaceCurrency));
  return formatMoney(intl, new Money(valueInSubunits, marketplaceCurrency));
};

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl, marketplaceCurrency } =
    props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const listingType = publicData?.listingType;

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig =>
      fieldConfig.showConfig?.displayOnListingPage !== false &&
      !['pueblo_servicio', 'municipalityIds', 'direccion_barrio_referencia'].includes(
        fieldConfig.key
      ) &&
      !(listingType === 'schedule-services' && fieldConfig.key === 'modalidad_servicio')
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const isNegotiationRequest = ['request-quote', 'pedir-cotizacion'].includes(listingType);

  const requestedScheduleFieldConfigs = isNegotiationRequest
    ? [{ key: 'requestedDate' }, { key: 'requestedTimeWindow' }]
    : [];
  const municipalityOptions = getPuertoRicoMunicipalityOptions();
  const normalizedMunicipalityFieldConfigs = [
    ...(isNegotiationRequest && publicData?.pueblo_servicio
      ? [
          {
            key: 'pueblo_servicio',
            schemaType: 'enum',
            enumOptions: municipalityOptions,
            showConfig: {
              isDetail: true,
              label: intl.formatMessage({ id: 'MunicipalityFields.customerLabel' }),
            },
          },
        ]
      : []),
  ];
  const normalizedAddressFieldConfigs = [
    ...(isNegotiationRequest && publicData?.direccion_barrio_referencia
      ? [
          {
            key: 'direccion_barrio_referencia',
            schemaType: 'long',
            showConfig: {
              isDetail: true,
              label: intl.formatMessage({ id: 'CustomerServiceLocationFields.addressLabel' }),
            },
          },
        ]
      : []),
  ];
  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: [
      ...displayableFieldConfigs,
      ...requestedScheduleFieldConfigs,
      ...normalizedMunicipalityFieldConfigs,
      ...normalizedAddressFieldConfigs,
    ],
    heading: 'ListingPage.detailsTitle',
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;

    if (key === 'requestedDate' && publicData?.requestedDate) {
      const date = new Date(`${publicData.requestedDate}T12:00:00`);
      const value = intl.formatDate(date, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      return filteredConfigs.concat({
        key,
        label: intl.formatMessage({ id: 'ListingPage.requestedDateLabel' }),
        value,
      });
    }

    if (key === 'requestedTimeWindow' && publicData?.requestedTimeWindow) {
      const window = formatRequestedTimeWindow(publicData.requestedTimeWindow);

      if (!window) {
        return filteredConfigs;
      }

      return filteredConfigs.concat({
        key,
        label: intl.formatMessage({ id: 'ListingPage.requestedTimeWindowLabel' }),
        value: intl.formatMessage(
          { id: 'ListingPage.requestedTimeWindowValue' },
          { start: window.start, end: window.end }
        ),
      });
    }

    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const isBudgetField = key === 'presupuesto_disponible';
      const displayedValue = isBudgetField
        ? formatBudgetAvailable(value, intl, marketplaceCurrency)
        : value;
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        displayedValue,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  const scheduleMunicipalityNames =
    listingType === 'schedule-services'
      ? (publicData?.municipalityIds || []).map(getPuertoRicoMunicipalityName).filter(Boolean)
      : [];

  return (
    <>
      <CustomExtendedDataSection
        sectionDetailsProps={sectionDetailsProps}
        propsForCustomFields={propsForCustomFields}
        idPrefix="listingPage"
        pickExtendedDataFields={pickExtendedDataFields}
      />
      {listingType === 'schedule-services' ? (
        <section className={css.municipalities}>
          <h2 className={css.heading}>
            {intl.formatMessage({ id: 'MunicipalityFields.providerListingLabel' })}
          </h2>
          <p className={css.serviceMode}>
            {intl.formatMessage({ id: 'MunicipalityFields.homeServiceMode' })}
          </p>
          {scheduleMunicipalityNames.length ? (
            <ul className={css.chips}>
              {scheduleMunicipalityNames.map(name => (
                <li className={css.chip} key={name}>
                  {name}
                </li>
              ))}
            </ul>
          ) : (
            <p>{intl.formatMessage({ id: 'MunicipalityFields.oldListingPublicHelp' })}</p>
          )}
        </section>
      ) : null}
    </>
  );
};

export default CustomListingFields;
